import { upperCase } from 'lodash-es';
import { CHART_COLORS_SPARROW, type SeriesData } from '@/common/utils';
import type { MetricResponse } from '@/api-client/data/model';
import { MULTI_STATS_DATA_ID } from './additionalChart.define';
import type { MetricChartInfo } from './metric.types';

const ALERT_SERIES_COLORS: Record<string, string> = {
  alert_critical_count: '#FF1F3F',
  alert_warning_count: '#FFD759',
  alert_normal_count: '#12A480',
};

const QUERY_MANAGER_PREFIX = 'qm_';
const CUSTOM_STAT_PREFIX = 'cm_';

export const isQueryManagerStatId = (statId?: string) => statId?.startsWith(QUERY_MANAGER_PREFIX);
export const isCustomStatId = (statId?: string) => statId?.startsWith(CUSTOM_STAT_PREFIX);

export const getChartRotatedColor = (
  chartColorList: Record<string, string> | string[],
  idx: number,
) => {
  let CHART_COLORS: string[] = [];
  if (chartColorList instanceof Array) {
    CHART_COLORS = chartColorList;
  } else {
    CHART_COLORS = Object.values(chartColorList);
  }

  return CHART_COLORS[idx % CHART_COLORS.length];
};

const getAlertSeries = (response: MetricResponse): SeriesData => {
  const dataId = response.dataDefinition?.dataId ?? '';

  return {
    color: ALERT_SERIES_COLORS[dataId],
    fillColor: ALERT_SERIES_COLORS[dataId],
    interpolation: 'none',
  };
};

export const getMetricSeriesDataFormatter =
  ({
    metricInfo,
    chartColorSet,
    isLinearInterpolation,
  }: {
    metricInfo: MetricChartInfo;
    chartColorSet?: Record<string, string> | string[];
    isLinearInterpolation?: boolean;
  }) =>
  ({
    index,
    response,
  }: {
    seriesId: string;
    metricIndex: number;
    index: number;
    responseIndex: number;
    response: MetricResponse;
  }): SeriesData => {
    if (metricInfo.rowKey === MULTI_STATS_DATA_ID.ALERT) {
      return getAlertSeries(response);
    }
    const color = getChartRotatedColor(chartColorSet ?? CHART_COLORS_SPARROW, index);
    return { color, fillColor: color, interpolation: isLinearInterpolation ? 'linear' : 'none' };
  };

export const getMetricSeriesId = ({
  metricInfo,
  dataId,
  summaryType,
  targetId,
  isMultiTarget,
}: {
  metricInfo: Omit<MetricChartInfo, 'dbType'>;
  dataId?: string;
  summaryType?: string;
  targetId?: string;
  isMultiTarget: boolean;
}) => {
  const isMultiMetric = (metricInfo.dataIds?.length ?? 0) > 1;
  const isMultiSummaryType = (metricInfo.summaryType?.length ?? 0) > 1;
  const isAlertMetric = metricInfo.rowKey === MULTI_STATS_DATA_ID.ALERT;
  const showSummaryName = summaryType && summaryType !== 'current';

  if (isMultiTarget || isAlertMetric) {
    return `${targetId ?? ''}_${dataId ?? ''}`;
  }

  if ((isMultiMetric && isMultiSummaryType) || showSummaryName) {
    return `${dataId ?? ''}_${summaryType ?? ''}`;
  }

  if (isMultiMetric) {
    return dataId ?? '';
  }

  return `${targetId ?? ''}_${summaryType ?? ''}`;
};

export const getMetricSeriesName = ({
  metricInfo,
  response,
  targetName,
  isMultiTarget,
  alias,
}: {
  metricInfo: Omit<MetricChartInfo, 'dbType'>;
  response: MetricResponse;
  targetName?: string;
  isMultiTarget: boolean;
  alias?: {
    use?: boolean;
    targetAlias?: string;
  };
}) => {
  const { displayName = '', unit } = response.dataDefinition || {};
  const displayNameWithUnit = unit ? `${displayName} (${unit})` : displayName || '';

  const isMultiMetric = (metricInfo.dataIds?.length || 0) > 1;
  const isMultiSummaryType = (metricInfo.summaryType?.length || 0) > 1;
  const isAlertMetric = metricInfo.rowKey === MULTI_STATS_DATA_ID.ALERT;
  const showSummaryName = response.summaryType && response.summaryType !== 'current';

  switch (true) {
    case isAlertMetric:
    case isMultiTarget: {
      if (alias?.use) {
        return alias.targetAlias || targetName || '';
      }
      return targetName ?? '';
    }
    case isMultiMetric && (isMultiSummaryType || showSummaryName):
      return `${displayNameWithUnit} - ${upperCase(response.summaryType)}`;
    case isMultiMetric:
      return displayNameWithUnit;
    case isMultiSummaryType || showSummaryName:
      return upperCase(response.summaryType);
    default:
      return displayNameWithUnit;
  }
};
